import { calendarDateInZone, familyRoleAtLeast, shiftCalendarDate } from '@baby-tracker/validation';
import { Baby } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router';

import { routePaths } from '../app/route-paths.js';
import {
    ExportSection,
    MedicationSection,
    MilestoneSection,
    ReminderSection,
} from '../components/features/insights/CareSections.js';
import { PhotosSection } from '../components/features/insights/PhotosSection.js';
import { GrowthSection } from '../components/features/insights/GrowthSection.js';
import { TrendCards } from '../components/features/insights/TrendCards.js';
import styles from '../components/features/tracking/tracking.module.css';
import { PageHeader } from '../components/layout/PageHeader.js';
import { Button } from '../components/ui/Button.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { errorText } from '../components/ui/error-text.js';
import { ErrorState } from '../components/ui/ErrorState.js';
import { SegmentedControl } from '../components/ui/SegmentedControl.js';
import { Spinner } from '../components/ui/Spinner.js';
import { useActiveChild } from '../hooks/useActiveChild.js';
import { useActiveFamily } from '../hooks/useActiveFamily.js';
import { useChildren } from '../hooks/useChildren.js';
import { useFamily } from '../hooks/useFamily.js';
import { useInsights } from '../hooks/useRecords.js';
import { useDayRollups } from '../hooks/useTracking.js';

export function InsightsPage(): ReactElement {
    const { active, me } = useActiveFamily();
    const family = useFamily(active?.id);
    const children = useChildren(active?.id, false);
    const { activeChild, setActiveChildId } = useActiveChild(children.data);
    const [days, setDays] = useState<7 | 30>(7);
    const today = family.data ? calendarDateInZone(new Date(), family.data.timeZone) : undefined;
    const from = today === undefined ? undefined : shiftCalendarDate(today, 1 - days);
    const rollups = useDayRollups(active?.id, from, today);
    const insights = useInsights(active?.id, activeChild?.id, days);
    const canLog = active !== undefined && familyRoleAtLeast(active.role, 'caregiver');
    const loading = me.isPending || family.isLoading || children.isLoading;
    const loadError = family.error ?? children.error;

    return (
        <>
            <PageHeader
                title="Insights"
                subtitle={activeChild ? `${activeChild.name} · last ${days} days` : undefined}
            />
            {loading ? <Spinner label="Loading insights" /> : null}
            {loadError ? (
                <ErrorState title="Could not load insights" description={errorText(loadError)} />
            ) : null}
            {children.data?.length === 0 ? (
                <EmptyState
                    icon={Baby}
                    title="No child profiles yet"
                    description="Add a child before reviewing growth and trends."
                    action={
                        active?.role === 'owner' ? (
                            <Link to={routePaths.settings}>Add a child</Link>
                        ) : undefined
                    }
                />
            ) : null}
            {(children.data?.length ?? 0) > 1 ? (
                <div className={styles.children} role="group" aria-label="Children">
                    {children.data?.map((child) => (
                        <Button
                            key={child.id}
                            variant={child.id === activeChild?.id ? 'primary' : 'secondary'}
                            aria-pressed={child.id === activeChild?.id}
                            onClick={() => {
                                setActiveChildId(child.id);
                            }}
                        >
                            {child.name}
                        </Button>
                    ))}
                </div>
            ) : null}
            {activeChild && family.data ? (
                <>
                    <SegmentedControl
                        label="Range"
                        value={days === 30 ? '30' : '7'}
                        options={[
                            { value: '7', label: '7 days' },
                            { value: '30', label: '30 days' },
                        ]}
                        onChange={(value) => {
                            setDays(value === '30' ? 30 : 7);
                        }}
                    />
                    {insights.error ? (
                        <ErrorState
                            title="Could not summarise activity"
                            description={errorText(insights.error)}
                        />
                    ) : null}
                    {insights.data ? (
                        <TrendCards
                            insights={insights.data}
                            unit={family.data.unitVolume}
                            days={rollups.data}
                            childId={activeChild.id}
                        />
                    ) : null}
                    <GrowthSection
                        familyId={family.data.id}
                        child={activeChild}
                        timeZone={family.data.timeZone}
                        massUnit={family.data.unitMass}
                        lengthUnit={family.data.unitLength}
                        canLog={canLog}
                    />
                    <PhotosSection
                        familyId={family.data.id}
                        childId={activeChild.id}
                        canLog={canLog}
                    />
                    <MilestoneSection
                        familyId={family.data.id}
                        childId={activeChild.id}
                        timeZone={family.data.timeZone}
                        canLog={canLog}
                    />
                    <MedicationSection
                        familyId={family.data.id}
                        childId={activeChild.id}
                        timeZone={family.data.timeZone}
                        canLog={canLog}
                    />
                    <ReminderSection
                        familyId={family.data.id}
                        childId={activeChild.id}
                        canLog={canLog}
                    />
                    <ExportSection familyId={family.data.id} familyName={family.data.name} />
                </>
            ) : null}
        </>
    );
}
